import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, RotateCcw, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Lote 167 — Identidade do escritório (ou da conta): nome + logo opcional que o CLIENTE vê nos e-mails de lembrete,
// na página de confirmação, na assinatura do WhatsApp e nas planilhas. O padrão é o nome do escritório; o logo é opcional.
// O IApauta fica só como "Enviado via IApauta". E-mails de segurança (senha, convites) seguem com a marca do app.
const LADO = 256;

// Reduz no navegador antes de enviar (o servidor confere de novo). Se não der para decodificar, manda o arquivo como está.
async function reduzir(file) {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    URL.revokeObjectURL(url);
    const k = Math.min(1, LADO / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.naturalWidth * k)); c.height = Math.max(1, Math.round(img.naturalHeight * k));
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    return blob && blob.size > 0 ? new File([blob], "logo.png", { type: "image/png" }) : file;
  } catch { return file; }
}

export function CabecalhoPrevia({ nome, logoUrl, marca = "IApauta" }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="identidade-previa" aria-label="Como o cliente vê">
      <div className="flex items-center gap-3">
        {logoUrl ? <img src={logoUrl} alt="" className="h-11 w-auto max-w-[160px] object-contain" data-testid="identidade-previa-logo" /> : null}
        <span className="text-lg font-bold text-stone-900 break-words min-w-0" data-testid="identidade-previa-nome">{nome}</span>
      </div>
      <p className="mt-3 text-sm text-stone-700">Olá, Maria. Lembrete de audiência em 20/11 às 10:00…</p>
      <div className="mt-4 border-t border-stone-200 pt-3">
        <p className="text-xs text-stone-700">Atenciosamente,<br /><b>{nome}</b></p>
        <p className="mt-2 text-[11px] text-stone-400">Enviado via {marca}</p>
      </div>
    </div>
  );
}

export default function IdentidadeCard({ onMudou }) {
  const { checkAuth: refresh } = useAuth() || {};
  const [st, setSt] = useState(null);
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  const arq = useRef(null);

  const aplicar = useCallback((d) => { setSt(d); setNome(d.nome_personalizado ? d.nome : ""); }, []);
  useEffect(() => { http.get("/identidade").then((r) => aplicar(r.data)).catch(() => setSt({ erro: true })); }, [aplicar]);

  const depois = async (d) => {
    aplicar(d);
    if (refresh) { try { await refresh(); } catch { /* o menu se atualiza no próximo carregamento */ } }
    if (onMudou) onMudou(d);
  };
  const falha = (e, padrao) => toast.error(e?.response?.data?.detail || padrao);

  const salvarNome = async (valor) => {
    setBusy(true);
    try { const r = await http.put("/identidade", { nome: valor }); await depois(r.data); toast.success(valor ? "Nome salvo" : "Voltou ao nome padrão"); }
    catch (e) { falha(e, "Não foi possível salvar o nome"); } finally { setBusy(false); }
  };
  const enviar = async (ev) => {
    const f = ev.target.files?.[0]; ev.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("arquivo", await reduzir(f));
      const r = await http.post("/identidade/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await depois(r.data); toast.success("Logo salvo");
    } catch (e) { falha(e, "Não foi possível enviar o logo"); } finally { setBusy(false); }
  };
  const removerLogo = async () => {
    setBusy(true);
    try { const r = await http.delete("/identidade/logo"); await depois(r.data); toast.success("Logo removido"); }
    catch (e) { falha(e, "Não foi possível remover o logo"); } finally { setBusy(false); }
  };

  if (!st) return <p className="text-sm text-stone-500" data-testid="identidade-carregando">Carregando…</p>;
  if (st.erro) return <p className="text-sm text-rose-700" data-testid="identidade-erro">Não foi possível carregar a identidade agora.</p>;

  const pode = st.pode_editar;
  const mudouNome = (nome.trim() || "") !== (st.nome_personalizado ? st.nome : "");
  const previaNome = nome.trim() || st.nome_padrao || st.nome;
  const previaLogo = st.tem_logo ? st.logo_url : null;
  const individual = st.origem === "individual";

  return (
    <div className="grid gap-5 md:grid-cols-[1fr_minmax(0,22rem)] items-start" data-testid="identidade-card">
      <div className="space-y-5 min-w-0">
        <p className="text-sm text-stone-600" data-testid="identidade-explica">
          {individual
            ? "Se quiser, seus clientes passam a ver o nome do seu escritório (e o logo) nos avisos, na página de confirmação e nas planilhas. Sem isso, os avisos saem com a marca do IApauta."
            : "O cliente vê o nome do escritório (e o logo, se você colocar) nos avisos, na página de confirmação e nas planilhas. O IApauta aparece só como “Enviado via IApauta”."}
        </p>
        <div>
          <label htmlFor="identidade-nome" className="block text-sm font-medium text-stone-800 mb-1.5">Nome nos avisos</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input id="identidade-nome" data-testid="identidade-nome" value={nome} disabled={!pode || busy} maxLength={st.nome_max || 80}
              onChange={(e) => setNome(e.target.value)} placeholder={st.nome_padrao || "Nome do escritório"} className="sm:max-w-sm" />
            {pode && (
              <div className="flex gap-2">
                <Button type="button" size="sm" disabled={busy || !mudouNome} data-testid="identidade-salvar-nome" onClick={() => salvarNome(nome.trim() || null)} className="bg-stone-900 text-white hover:bg-stone-800 gap-1.5">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Salvar nome
                </Button>
                {st.nome_personalizado && (
                  <Button type="button" size="sm" variant="outline" disabled={busy} data-testid="identidade-nome-padrao" onClick={() => salvarNome(null)} className="gap-1.5">
                    <RotateCcw className="w-3.5 h-3.5" /> Usar o padrão
                  </Button>
                )}
              </div>
            )}
          </div>
          <p className="mt-1.5 text-xs text-stone-500" data-testid="identidade-nome-dica">
            {st.nome_padrao ? `Padrão: ${st.nome_padrao}.` : "Escreva como o cliente conhece o escritório."}
          </p>
        </div>
        <div>
          <span className="block text-sm font-medium text-stone-800 mb-1.5">Logo (opcional)</span>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={arq} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="identidade-arquivo" onChange={enviar} />
            {pode && (
              <Button type="button" size="sm" variant="outline" disabled={busy} data-testid="identidade-enviar-logo" onClick={() => arq.current?.click()} className="gap-1.5">
                <ImagePlus className="w-3.5 h-3.5" /> {st.tem_logo ? "Trocar logo" : "Enviar logo"}
              </Button>
            )}
            {pode && st.tem_logo && (
              <Button type="button" size="sm" variant="outline" disabled={busy} data-testid="identidade-remover-logo" onClick={removerLogo} className="gap-1.5 text-rose-700 hover:text-rose-800">
                <Trash2 className="w-3.5 h-3.5" /> Remover logo
              </Button>
            )}
            {!st.tem_logo && <span className="text-xs text-stone-500" data-testid="identidade-sem-logo">Sem logo: os avisos levam só o nome.</span>}
          </div>
          <p className="mt-1.5 text-xs text-stone-500">PNG, JPG ou WebP, até 400 KB. Fundo transparente fica melhor. A imagem é reduzida e guardada sem dados da câmera.</p>
        </div>
        {!pode && (
          <p className="text-xs text-stone-500 rounded-md bg-stone-50 border border-stone-200 px-3 py-2" data-testid="identidade-somente-leitura">
            Só administradores do escritório podem mudar o nome e o logo.
          </p>
        )}
        <p className="text-xs text-stone-500">E-mails de segurança (senha, confirmação de cadastro, convites) continuam com a marca do IApauta.</p>
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1.5">Como o cliente vê</p>
        <CabecalhoPrevia nome={previaNome} logoUrl={previaLogo} marca={st.marca_app || "IApauta"} />
      </div>
    </div>
  );
}
